/*
 * PROD-7828 R8 — pure form logic for the Additional payments tab: parsing, validation, payload
 * building and the pre-save warnings. No React, no network, no clock and no id generator of its own
 * (both are passed in), so every branch is unit-tested in form_logic.test.js.
 *
 * D61: Ops can only ADD here — the amount field is a positive magnitude and entry_type is always
 * ADDITION. Deductions are penalty-section work (R6); the only negative row is the SYSTEM
 * carry-forward, which Ops can waive but never create or edit.
 */
import {
  ENTRY_TYPE,
  ERROR,
  LIMITS,
  LIST_STATUS,
  POLICY_FALLBACK,
  REFERENCE_ID_RE,
  CARRY_FORWARD,
  is_pay_month_in_window,
  month_key,
  WARNING
} from './params';
import { ist_day } from '../shared/ist';

const AMOUNT_RE = /^\d+(\.\d{1,2})?$/;

// seed: fields carried into a fresh form (previous_month_carry); the request id and date stay new.
export const blank_form = (pay_month, now, new_request_id, seed = null) => ({
  client_request_id: new_request_id(),
  pay_month,
  teacher_uuid: '',
  teacher_name: '',
  teacher_has_base_rate: null,
  category: '',
  amount: '',
  entry_date: ist_day(now),
  entry_time: '',
  note: '',
  reference_id: '',
  related_session_uuid: '',
  related_session_label: '',
  ...seed
});

// 'YYYY-MM-DD HH:MM' -> the two form fields; a 00:00 time is the "no time given" default, so it
// round-trips as blank rather than re-asserting a time the operator never typed.
export const split_entry_at = (entry_at_ist) => {
  const m =
    typeof entry_at_ist === 'string' &&
    entry_at_ist.match(/^(\d{4}-\d{2}-\d{2})(?: (\d{2}:\d{2}))?/);
  if (!m) return { entry_date: '', entry_time: '' };
  return { entry_date: m[1], entry_time: m[2] && m[2] !== '00:00' ? m[2] : '' };
};

export const edit_form = (row) => ({
  uuid: row.uuid,
  version: row.version,
  pay_month: row.pay_month,
  teacher_uuid: row.teacher_uuid,
  teacher_name: row.teacher_name,
  teacher_has_base_rate: null,
  category: row.category,
  amount: String(Math.abs(Number(row.amount))),
  note: row.note || '',
  reference_id: row.reference_id || '',
  related_session_uuid: row.related_session_uuid || '',
  related_session_label: row.related_session_uuid || '',
  ...split_entry_at(row.entry_at_ist)
});

// Positive magnitude, at most 2 dp, at most the EFFECTIVE cap (a policy row, defaulted). The server
// re-checks every rule and stays the authority.
export const parse_amount = (text, limits = POLICY_FALLBACK) => {
  const raw = String(text === null || text === undefined ? '' : text).trim();
  if (!AMOUNT_RE.test(raw)) return { ok: false, value: null, code: ERROR.INVALID_AMOUNT };
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) {
    return { ok: false, value: null, code: ERROR.INVALID_AMOUNT };
  }
  if (value > limits.max_entry_inr) return { ok: false, value, code: ERROR.AMOUNT_ABOVE_CAP };
  return { ok: true, value, code: '' };
};

export const is_valid_entry_date = (date) => {
  if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};

export const is_valid_time = (time) => time === '' || /^([01]\d|2[0-3]):[0-5]\d$/.test(time);

// Ops-selectable categories: SYSTEM-only codes are never offered, and the entry type is ADDITION.
export const category_options = (categories) =>
  (Array.isArray(categories) ? categories : [])
    .filter(
      (c) =>
        c &&
        c.system_only !== true &&
        (Array.isArray(c.allowed_entry_types) ? c.allowed_entry_types : []).indexOf(
          ENTRY_TYPE.ADDITION
        ) !== -1
    )
    .map((c) => ({ value: c.code, label: c.label }));

/*
 * CD-113 (D106): a previous-month entry refused for want of that month's record is re-entered in the
 * current month as a "Previous month payment adjustment" (the code the carry-forward shares, Q6).
 * null when the catalogue does not offer that category to Ops.
 */
export const previous_month_carry = (form, categories) =>
  category_options(categories).some((o) => o.value === CARRY_FORWARD.CATEGORY)
    ? {
        teacher_uuid: form.teacher_uuid,
        teacher_name: form.teacher_name,
        teacher_has_base_rate: form.teacher_has_base_rate,
        amount: form.amount,
        note: form.note,
        category: CARRY_FORWARD.CATEGORY
      }
    : null;

export const category_label = (categories, code) => {
  const hit = (Array.isArray(categories) ? categories : []).find((c) => c && c.code === code);
  return hit ? hit.label : code || '';
};

const reference_error = (reference_id) => {
  if (!reference_id) return '';
  if (!REFERENCE_ID_RE.test(reference_id)) return ERROR.INVALID_REFERENCE_ID;
  if (reference_id.indexOf(CARRY_FORWARD.REFERENCE_PREFIX) === 0)
    return ERROR.RESERVED_REFERENCE_ID;
  return '';
};

/*
 * mode 'create' needs a teacher and a note; mode 'edit' keeps teacher and pay_month immutable
 * (Q13) and needs a reason instead (Q12: always, not only when the amount changes). The Q15 window
 * limits where an entry is ADDED; an edit's month is fixed and the server decides whether it is open.
 * Lengths count UTF-16 units, the same unit as the inputs' maxLength; goserver counts runes, so a
 * client-accepted text is never longer than the server allows.
 */
export const validate_form = (
  form,
  { now, categories, mode = 'create', reason = '', limits = POLICY_FALLBACK }
) => {
  const errors = {};
  const selectable = category_options(categories).map((o) => o.value);

  if (mode === 'create' && !form.teacher_uuid) errors.teacher_uuid = ERROR.INVALID_REQUEST;
  if (mode === 'create' && !is_pay_month_in_window(form.pay_month, now)) {
    errors.pay_month = ERROR.PAY_MONTH_OUTSIDE_WINDOW;
  }
  if (!form.category) errors.category = ERROR.UNKNOWN_CATEGORY;
  else if (selectable.indexOf(form.category) === -1) {
    // A code the catalogue marks system_only can never be saved by Ops (carry-forward).
    errors.category = (Array.isArray(categories) ? categories : []).some(
      (c) => c && c.code === form.category && c.system_only === true
    )
      ? ERROR.SYSTEM_CATEGORY_NOT_SELECTABLE
      : ERROR.UNKNOWN_CATEGORY;
  }

  const amount = parse_amount(form.amount, limits);
  if (!amount.ok) errors.amount = amount.code;

  if (!is_valid_entry_date(form.entry_date) || !is_valid_time(form.entry_time)) {
    errors.entry_date = ERROR.INVALID_ENTRY_DATE;
  }

  const note = (form.note || '').trim();
  if (mode === 'create' && (!note || note.length > LIMITS.NOTE_MAX_LEN)) {
    errors.note = ERROR.NOTE_REQUIRED;
  }
  if (mode === 'edit' && note.length > LIMITS.NOTE_MAX_LEN) errors.note = ERROR.NOTE_REQUIRED;

  const ref_err = reference_error((form.reference_id || '').trim());
  if (ref_err) errors.reference_id = ref_err;

  const trimmed_reason = (reason || '').trim();
  if (mode === 'edit' && (!trimmed_reason || trimmed_reason.length > LIMITS.REASON_MAX_LEN)) {
    errors.reason = ERROR.REASON_REQUIRED;
  }

  return { ok: Object.keys(errors).length === 0, errors, amount: amount.value };
};

export const validate_reason = (reason) => {
  const trimmed = (reason || '').trim();
  return trimmed && trimmed.length <= LIMITS.REASON_MAX_LEN
    ? { ok: true, reason: trimmed, code: '' }
    : { ok: false, reason: trimmed, code: ERROR.REASON_REQUIRED };
};

/*
 * Both confirmations are wire fields the server REQUIRES to clear its own 409s: confirm_large clears
 * CONFIRM_REQUIRED (amount >= the confirm threshold) and confirm_duplicate clears POSSIBLE_DUPLICATE.
 * They are only ever true because the operator accepted the matching dialog.
 */
export const confirm_flags = ({ confirm_duplicate, confirm_large } = {}) => ({
  confirm_duplicate: !!confirm_duplicate,
  confirm_large: !!confirm_large
});

export const create_payload = (form, confirms = {}) => ({
  client_request_id: form.client_request_id,
  teacher_uuid: form.teacher_uuid,
  pay_month: form.pay_month,
  entry_type: ENTRY_TYPE.ADDITION,
  category: form.category,
  amount: parse_amount(form.amount).value,
  entry_date: form.entry_date,
  entry_time: form.entry_time || '',
  note: (form.note || '').trim(),
  reference_id: (form.reference_id || '').trim(),
  related_session_uuid: form.related_session_uuid || '',
  ...confirm_flags(confirms)
});

export const update_payload = (form, reason, confirms = {}) => ({
  uuid: form.uuid,
  version: form.version,
  entry_type: ENTRY_TYPE.ADDITION,
  category: form.category,
  amount: parse_amount(form.amount).value,
  entry_date: form.entry_date,
  entry_time: form.entry_time || '',
  note: (form.note || '').trim(),
  reference_id: (form.reference_id || '').trim(),
  related_session_uuid: form.related_session_uuid || '',
  reason: (reason || '').trim(),
  ...confirm_flags(confirms)
});

export const remove_payload = (row, reason) => ({
  uuid: row.uuid,
  version: row.version,
  reason: (reason || '').trim()
});

// Nothing changed -> the server writes no audit and does not recompute, so the button stays off.
export const edit_changed = (form, row) => {
  const seed = edit_form(row);
  return [
    'category',
    'amount',
    'entry_date',
    'entry_time',
    'note',
    'reference_id',
    'related_session_uuid'
  ].some((k) => String(form[k] || '') !== String(seed[k] || ''));
};

// Q16/Q17: a big amount and a look-alike entry both get a confirmation dialog, never a hard block.
// Mirrors the server's >= confirm_above_inr test on the EFFECTIVE threshold, on create and on edit.
export const needs_amount_confirm = (form, limits = POLICY_FALLBACK) => {
  const amount = parse_amount(form.amount, limits);
  return amount.ok && amount.value >= limits.confirm_above_inr;
};

export const duplicate_matches = (form, rows) => {
  const amount = parse_amount(form.amount);
  if (!amount.ok) return [];
  return (Array.isArray(rows) ? rows : []).filter(
    (r) =>
      r &&
      r.status === LIST_STATUS.ACTIVE &&
      r.uuid !== form.uuid &&
      r.teacher_uuid === form.teacher_uuid &&
      r.category === form.category &&
      Number(r.amount) === amount.value
  );
};

/*
 * Entries the server said were ALREADY SAVED (IDEMPOTENCY_MISMATCH) for this form's teacher and month.
 * Any amount and category: the likely correction after a lost response is a changed amount, which the
 * look-alike check (same category and amount) cannot see.
 */
export const saved_entry_conflicts = (form, saved_entries) =>
  (Array.isArray(saved_entries) ? saved_entries : []).filter(
    (e) =>
      e &&
      e.status === LIST_STATUS.ACTIVE &&
      e.uuid !== form.client_request_id &&
      e.teacher_uuid === form.teacher_uuid &&
      month_key(e.pay_month) === month_key(form.pay_month)
  );

export const entry_date_outside_pay_month = (form) =>
  is_valid_entry_date(form.entry_date) &&
  !!month_key(form.pay_month) &&
  month_key(form.entry_date) !== month_key(form.pay_month);

// Pre-save, non-blocking (Q20/Q14). Codes are the backend's; the server repeats its own on save.
export const pre_save_warnings = (form, month_text) => {
  const out = [];
  if (entry_date_outside_pay_month(form)) {
    out.push({
      code: WARNING.ENTRY_DATE_OUTSIDE_PAY_MONTH,
      message: `The entry date is outside the payment month — the money is still paid in ${month_text}.`
    });
  }
  if (form.teacher_has_base_rate === false) {
    out.push({
      code: WARNING.TEACHER_HAS_NO_BASE_RATE,
      message:
        'This teacher has no base rate, so the month cannot be computed until one is set — the entry saves, but the month will show "needs review".'
    });
  }
  return out;
};
